/**
 * IRR Decomposition Waterfall Chart for Company Detail Page.
 *
 * Renders a Highcharts waterfall chart showing the breakdown of IRR components:
 * Revenue growth, Margin change, Share count change, Multiple re-rating,
 * Dividend yield, Cross-term, and Total.
 */

import type Highcharts from "highcharts";
import { isDarkMode, setupThemeObserver } from "../../common/chartTheme";
import { createChartLifecycle } from "./chartLifecycle";

/**
 * IRR decomposition component data from the template.
 */
export interface IRRComponent {
  label: string;
  value: number | null;
  tooltip: string;
  key: string;
}

/**
 * IRR decomposition data structure from the template filter.
 */
export interface IRRDecompositionData {
  components: IRRComponent[];
  total_irr: number | null;
  method_label: string;
}

let themeObserver: MutationObserver | null = null;

function renderChartForContainer(container: HTMLElement): void {
  const dataAttr = container.dataset.decomposition;
  if (!dataAttr) {
    lifecycle.destroyChart(container.id);
    return;
  }

  try {
    const data = JSON.parse(dataAttr) as IRRDecompositionData;
    if (!data?.components || data.components.length === 0) {
      lifecycle.destroyChart(container.id);
      return;
    }

    lifecycle.destroyChart(container.id);
    const chart = renderIRRDecompositionChart(container.id, data);
    if (chart) {
      lifecycle.instances.set(container.id, chart);
    }
  } catch (e) {
    console.warn(`Failed to parse IRR decomposition data for ${container.id}:`, e);
  }
}

const lifecycle = createChartLifecycle('[id^="irr-decomposition-chart-"]', renderChartForContainer);

/**
 * Theme colors for the chart.
 */
interface ChartColors {
  textColor: string;
  subtitleColor: string;
  gridLineColor: string;
  positiveColor: string;
  negativeColor: string;
  totalColor: string;
  lineColor: string;
}

/**
 * Gets theme colors based on current dark mode state.
 */
function getThemeColors(): ChartColors {
  const dark = isDarkMode();
  return {
    textColor: dark ? "#E0E0E3" : "#333333",
    subtitleColor: dark ? "#A0A0A3" : "#666666",
    gridLineColor: dark ? "#374151" : "#e5e7eb",
    positiveColor: dark ? "#4ade80" : "#22c55e",
    negativeColor: dark ? "#f87171" : "#ef4444",
    totalColor: dark ? "#60a5fa" : "#3b82f6",
    lineColor: dark ? "#6b7280" : "#9ca3af",
  };
}

/**
 * Renders an IRR decomposition waterfall chart.
 *
 * @param containerId - The ID of the container element.
 * @param data - The IRR decomposition data.
 * @returns The Highcharts chart instance, or null if rendering failed.
 */
export function renderIRRDecompositionChart(
  containerId: string,
  data: IRRDecompositionData,
): Highcharts.Chart | null {
  const container = document.getElementById(containerId);
  if (!container || !window.Highcharts) {
    return null;
  }

  // Filter out components with null values and prepare data
  const validComponents = data.components.filter((c) => c.value !== null);

  if (validComponents.length === 0) {
    return null;
  }

  const colors = getThemeColors();

  const totalValue =
    data.total_irr !== null && data.total_irr !== undefined
      ? data.total_irr
      : validComponents.reduce((sum, c) => sum + (c.value ?? 0), 0);

  // Build waterfall data points
  const chartData: Highcharts.PointOptionsObject[] = validComponents.map((c) => ({
    name: c.label,
    y: c.value as number,
    color: (c.value as number) >= 0 ? colors.positiveColor : colors.negativeColor,
    custom: { tooltip: c.tooltip },
  }));

  // Add total as sum point
  chartData.push({
    name: "Total",
    isSum: true,
    color: colors.totalColor,
    custom: { displayValue: totalValue },
  });

  const chart = window.Highcharts.chart(containerId, {
    chart: {
      type: "waterfall",
      backgroundColor: "transparent",
      height: 280,
      spacing: [15, 15, 15, 10],
    },
    title: { text: undefined },
    credits: { enabled: false },
    exporting: { enabled: false },
    navigation: { buttonOptions: { enabled: false } },
    xAxis: {
      type: "category",
      labels: {
        style: {
          color: colors.subtitleColor,
          fontSize: "11px",
        },
        rotation: 0,
      },
      tickLength: 0,
      lineColor: colors.gridLineColor,
    },
    yAxis: {
      title: { text: undefined },
      labels: {
        format: "{value}%",
        style: {
          color: colors.subtitleColor,
          fontSize: "10px",
        },
      },
      gridLineColor: colors.gridLineColor,
      plotLines: [
        {
          value: 0,
          color: colors.lineColor,
          width: 1,
          zIndex: 3,
        },
      ],
    },
    legend: { enabled: false },
    tooltip: {
      useHTML: true,
      style: { fontSize: "12px" },
      formatter: function (this: Highcharts.TooltipFormatterContextObject): string {
        const point = this.point as Highcharts.Point & {
          isSum?: boolean;
          custom?: { tooltip?: string; displayValue?: number };
        };
        const tooltip = point.custom?.tooltip || "";
        const rawValue =
          point.isSum && point.custom?.displayValue !== undefined
            ? point.custom.displayValue
            : this.y;
        const value =
          rawValue !== undefined && rawValue !== null
            ? `${rawValue >= 0 ? "+" : ""}${rawValue.toFixed(1)}%`
            : "";
        return `<div style="padding:2px 0"><strong>${this.key}</strong><br/>${value}${tooltip ? `<br/><em style="color:${colors.subtitleColor};font-size:11px">${tooltip}</em>` : ""}</div>`;
      },
    },
    plotOptions: {
      waterfall: {
        dataLabels: {
          enabled: true,
          formatter: function (this: Highcharts.PointLabelObject): string {
            const point = (this.point ?? {}) as Highcharts.Point & {
              isSum?: boolean;
              custom?: { displayValue?: number };
            };
            const rawValue =
              point.isSum && point.custom?.displayValue !== undefined
                ? point.custom.displayValue
                : this.y;
            if (rawValue === undefined || rawValue === null) return "";
            return `${rawValue >= 0 ? "+" : ""}${rawValue.toFixed(1)}%`;
          },
          style: {
            fontSize: "10px",
            fontWeight: "600",
            textOutline: "none",
            color: colors.textColor,
          },
          verticalAlign: "top",
          y: -8,
        },
        borderWidth: 0,
        borderRadius: 3,
        lineWidth: 1,
        dashStyle: "ShortDot",
        lineColor: colors.lineColor,
        pointPadding: 0.15,
        groupPadding: 0.15,
        animation: { duration: 500 },
      },
    },
    series: [
      {
        type: "waterfall",
        name: "IRR",
        data: chartData,
        upColor: colors.positiveColor,
        color: colors.negativeColor,
      },
    ],
    responsive: {
      rules: [
        {
          condition: { maxWidth: 500 },
          chartOptions: {
            chart: { height: 220 },
            xAxis: { labels: { style: { fontSize: "9px" }, rotation: -25 } },
            plotOptions: {
              waterfall: {
                dataLabels: { style: { fontSize: "9px" }, y: -5 },
              },
            },
          },
        },
      ],
    },
  } as Highcharts.Options) as Highcharts.Chart;

  return chart;
}

/**
 * Initializes all IRR decomposition charts on the page.
 *
 * Finds all elements with IDs starting with "irr-decomposition-chart-"
 * and renders waterfall charts based on their data-decomposition attributes.
 */
export function initializeIRRDecompositionCharts(): void {
  const chartContainers = document.querySelectorAll<HTMLElement>(
    '[id^="irr-decomposition-chart-"]',
  );

  const activeIds = new Set<string>();

  chartContainers.forEach((container) => {
    activeIds.add(container.id);
    renderChartForContainer(container);
  });

  for (const containerId of lifecycle.instances.keys()) {
    if (!activeIds.has(containerId) || !document.getElementById(containerId)) {
      lifecycle.destroyChart(containerId);
    }
  }

  if (lifecycle.instances.size === 0) {
    if (themeObserver) {
      themeObserver.disconnect();
      themeObserver = null;
    }
    return;
  }

  if (!themeObserver) {
    themeObserver = setupThemeObserver(() => {
      initializeIRRDecompositionCharts();
    });
  }
}

/**
 * Registers DOM listeners to auto-initialize IRR decomposition charts.
 */
export function setupIRRDecompositionChartAutoInit(): void {
  lifecycle.setupAutoInit(initializeIRRDecompositionCharts);
}
